import * as React from 'react';
import { Suspense, useEffect, useMemo, useState } from 'react';

import { initialMoneyTransfer, IReqAccountTransfer, ITransferAccount, ITransferPurse } from "./interface";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { useGlobalContext } from "../../../services/context.service";
import SelectClient from "./Steps/SelectClient";
import { holderAPI } from "../../../services/holderAPI.service";
import { styled } from "@mui/material/styles";
import TransferButton from "./Steps/TransferButton";
import SourceSum from "./Steps/SourceSum";

import { ContainerStepItem } from './Steps/style';
import { Grid, Typography } from "@mui/material";
import { fnLocalizeFilter } from "../../../services/localize.service";
import LabelImportantIcon from '@mui/icons-material/LabelImportant';

import CreditCardIcon from "@mui/icons-material/CreditCard";
import PaymentsIcon from '@mui/icons-material/Payments';
import { handleTransferAccounts } from "../Setting/Tabs/User/utils/local.request.api";
import { useAppDispatch } from "../../../hooks/redux";
import Loading from "../../UI/Loading/Loading";
import Modal from "../../UI/Modal/Modal";

const ContainerTransfer = styled( Box )( () => ( {
	padding: "1rem",
	maxWidth: "500px"
} ) );

const BalanceLabel = styled( Typography )( () => ( {
	position: "absolute",
	top: ".5rem",
	right: ".5rem",
	display: "flex",
	alignItems: "center",
} ) );


export default function MoneyTransfers () {
	const { locale } = useGlobalContext();

	const [ transfer, setTransfer ] = useState<IReqAccountTransfer>( initialMoneyTransfer );

	const [ sourceRemark, setSourceRemark ] = useState<ITransferAccount & ITransferPurse>();
	const [ destinationRemark, setDestinationRemark ] = useState<ITransferAccount>();
	const [ isOpenModal, setOpenModal ] = useState( false );

	/* Получаем список клиентов доступных пользователю */
	const { data: dataHolder = { holders: [] } } = holderAPI.useFetchAllClientsQuery( { locale } );

	const [ holderId, setHolder ] = useState( -1 );

	useEffect( () => {
		dataHolder.holders.length > 0 && setHolder( dataHolder.holders[ 0 ].holderId );
	}, [ dataHolder.holders ] );


	const handleChangeAccount = ( type: 'sourceAccountId' | 'destinationAccountId', transferData: ITransferAccount & ITransferPurse ) => {

		let moneyTransfer = {};

		if ( type === 'sourceAccountId' ) {
			setSourceRemark( {
				accountId: transferData.accountId,
				accountRemark: transferData.accountRemark,
				totalBalance: transferData.totalBalance,
				purseId: transferData.purseId,
				remark: transferData.remark
			} )
			moneyTransfer = {
				sourceAccountId: transferData && transferData?.accountId ? transferData.accountId : -1,
				purseId: transferData.purseId
			};
		}

		if ( type === 'destinationAccountId' ) {
			setDestinationRemark( {
				accountId: transferData.accountId,
				accountRemark: transferData.accountRemark,
				totalBalance: transferData.totalBalance
			} )
			moneyTransfer = {
				destinationAccountId: transferData && transferData?.accountId ? transferData.accountId : -1,
			};
		}

		setTransfer( prev => ( { ...prev, ...moneyTransfer } ) )
	};

	const remarkSource = useMemo( () => {

		if ( !sourceRemark ) return (
			<ContainerStepItem>
				<CreditCardIcon/>
				<Typography
					sx={ { paddingLeft: ".5rem" } }> { fnLocalizeFilter( locale, '_lang.module.transfer.titleAccountSourceHelper' ) } </Typography>
			</ContainerStepItem>
		)

		return (
			<ContainerStepItem property={ JSON.stringify( { justifyContent: "flex-start" } ) }>
				<Grid sx={ { display: "flex", flexDirection: "column" } }>
					<Grid sx={ { display: "flex" } }>
						<CreditCardIcon/>
						<BalanceLabel>
							<PaymentsIcon sx={ { marginRight: ".2rem" } }/>
							{ sourceRemark.purseId === 0 ? sourceRemark.totalBalance.toFixed( 2 ) : sourceRemark.totalBalance }
						</BalanceLabel>

						<Typography sx={ { paddingLeft: ".5rem" } }>
							{ `ID: ${ sourceRemark.accountId }, ${ fnLocalizeFilter( locale, '_lang.module.setting.users.titleAccountRemark' ) }: "${ sourceRemark.accountRemark }"` }
						</Typography>
					</Grid>
					<Grid sx={ { display: "flex" } }>
						<LabelImportantIcon/>
						<Typography sx={ { paddingLeft: ".5rem" } }>
							{ `${ fnLocalizeFilter( locale, '_lang.module.reports.titlePurse' ) }: ${ sourceRemark.remark }` }
						</Typography>
					</Grid>
				</Grid>
			</ContainerStepItem>
		)
	}, [ sourceRemark ] );

	const remarkDestination = useMemo( () => {
		if ( !destinationRemark ) return (
			<ContainerStepItem>
				<CreditCardIcon/>
				<Typography
					sx={ { paddingLeft: ".5rem" } }> { fnLocalizeFilter( locale, '_lang.module.transfer.titleAccountDestinationHelper' ) } </Typography>
			</ContainerStepItem>
		)

		return (
			<ContainerStepItem property={ JSON.stringify( { justifyContent: "flex-start" } ) }>
				<CreditCardIcon/>
				<BalanceLabel>
					{
						transfer.purseId === 0 && destinationRemark && destinationRemark.totalBalance &&
              <>
                <PaymentsIcon sx={ { marginRight: ".2rem" } }/> { destinationRemark.totalBalance.toFixed( 2 ) }
              </>
					}

				</BalanceLabel>
				<Typography sx={ { paddingLeft: ".5rem" } }>
					{ `ID: ${ destinationRemark.accountId }, ${ fnLocalizeFilter( locale, '_lang.module.setting.users.titleAccountRemark' ) }: "${ destinationRemark.accountRemark }"` }
				</Typography>
			</ContainerStepItem>
		)
	}, [ destinationRemark, transfer.purseId ] );

	const dispatch = useAppDispatch();

	const handleTransferMoney = async () => {
		await handleTransferAccounts( {
			transferData: transfer,
			locale,
			holderId,
			dispatch
		} );
		setOpenModal( false );
	};

	const handleCheckDisabledCommit = useMemo( () => {
		let check = false;

		for ( const item of Object.values( transfer ) ) {
			if ( item <= -1 ) {
				check = true;
				break;
			}
		}

		if ( transfer.value === 0 ) check = true;
		if ( sourceRemark && sourceRemark.totalBalance === 0 ) check = true;
		if ( sourceRemark && sourceRemark.totalBalance < transfer?.value ) check = true;

		return check

	}, [ transfer ] );

	return (
		<>
			<ContainerTransfer>
				{
					dataHolder.holders.length > 0 &&
            <SelectClient
                holders={ dataHolder.holders }
                selectHolderId={ holderId }
                onSelectClient={ ( holderId ) => setHolder( holderId ) }
            />
				}

				<Typography
					sx={ { marginTop: "1rem" } }>{ fnLocalizeFilter( locale, '_lang.module.transfer.titleAccountSource' ) }</Typography>

				<TransferButton
					type={ 'source' }
					label={ remarkSource }
					onChange={ ( transfer ) => handleChangeAccount( 'sourceAccountId', transfer ) }
					holderId={ holderId }
				/>

				<SourceSum
					maxSum={ sourceRemark?.totalBalance }
					onChange={ ( num ) => setTransfer( prev => ( { ...prev, value: num } ) ) }
					value={ transfer.value || 0 }
				/>
				<Typography>{ fnLocalizeFilter( locale, '_lang.module.transfer.titleAccountDestination' ) }</Typography>
				<TransferButton
					type={ 'destination' }
					label={ remarkDestination }
					onChange={ ( transfer ) => handleChangeAccount( 'destinationAccountId', transfer ) }
					holderId={ holderId }
				/>

				<Button
					variant={ 'contained' }
					sx={ { width: "100%" } }
					disabled={ handleCheckDisabledCommit }
					onClick={ () => setOpenModal( true ) }
				>{ fnLocalizeFilter( locale, '_lang.module.transfer.titleTransferButton' ) }</Button>
			</ContainerTransfer>
			{
				isOpenModal && <Suspense fallback={ <Loading type={ 'fullScreen' }/> }>
            <Modal
                handleBtnOk={ handleTransferMoney }
                isOpen={ isOpenModal }
                handleBtnCancel={ () => setOpenModal( false ) }
            >
              <>{ fnLocalizeFilter( locale, '_lang.module.transfer.confirmTransfer' ) }</>
            </Modal>
          </Suspense>
			}
		</>
	)
}
